<template>
  <div class="container details__content__box mb-5">
    <div class="columns is-full-width">
      <div class="column is-one-quarter">
        <label for="airport">
          {{ $t("ReservationForm.departureAirport") }}
        </label>
      </div>
      <div class="column is-one-quarter">
        <label for="airport">{{ $t("ReservationForm.arrivalAirport") }}</label>
      </div>
      <div class="column is-one-quarter">
        <label for="departure-date">{{
          $t("ReservationForm.departureDate")
        }}</label>
      </div>
      <div class="column is-one-eighth">
        <label for="departure-date">{{ $t("dayPart") }}</label>
      </div>
      <div class="column is-one-eighth">
        <!-- Header space for action button -->
      </div>
    </div>
    <template v-if="newLegAllowed">
      <div class="columns is-full-width is-multiline">
        <div class="column is-one-quarter">
          <FindAirport @airport-selected="setDepartureAirport" />
        </div>
        <div class="column is-one-quarter">
          <FindAirport @airport-selected="setArrivalAirport" />
        </div>
        <div class="column is-one-quarter"></div>
        <div class="column is-one-eighth"></div>
        <div class="column is-one-eighth"></div>
      </div>
      <div class="columns is-full-width is-multiline">
        <div class="column is-one-quarter">
          {{ departureAirport?.name }}
        </div>
        <div class="column is-one-quarter">
          {{ arrivalAirport?.name }}
        </div>
        <div class="column is-one-quarter">
          <Datepicker
            v-model="departureDate"
            :enableTimePicker="false"
            autoApply
            :format="formatDate"
            :minDate="minDepartureDate"
            locale="nl"
          />
        </div>
        <div class="column is-one-eighth">
          <div class="select">
            <select v-model="selectedDayPart">
              <option
                v-for="dayPart in dayParts"
                :value="dayPart.value"
                :key="dayPart.value"
              >
                {{ dayPart.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="column is-one-eighth">
          <div class="is-flex is-justify-content-flex-end">
            <button
              class="button flight-leg-button"
              v-if="selectedRoundtripType !== 'roundTrip'"
              :disabled="!isValidLeg"
              @click.stop="addLeg"
            >
              <span>{{ $t("ReservationForm.AddFlightLeg") }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
    <div
      class="columns is-multiline"
      v-if="selectedRoundtripType === 'roundTrip' && newLegAllowed"
    >
      <div class="column is-one-quarter">
        {{ arrivalAirport?.name }}
      </div>
      <div class="column is-one-quarter">
        {{ departureAirport?.name }}
      </div>
      <div class="column is-one-quarter">
        <Datepicker
          v-model="departureDateReturnFlight"
          :enableTimePicker="false"
          autoApply
          :format="formatDate"
          :minDate="departureDate"
          locale="nl"
        />
      </div>
      <div class="column is-one-eighth">
        <div class="is-flex is-align-items-center" style="gap: 0.5rem">
          <div class="select">
            <select v-model="selectedReturnDayPart">
              <option disabled value="">
                {{ $t("ReservationForm.SelectDayPart") }}
              </option>
              <option
                v-for="dayPart in dayParts"
                :value="dayPart.value"
                :key="dayPart.value"
              >
                {{ dayPart.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="column is-one-eighth">
        <!-- Empty column for alignment -->
      </div>
    </div>
    <div class="columns is-full-width is-multiline" v-if="newLegAllowed">
      <div class="column is-one-quarter"></div>

      <div class="column is-one-quarter"></div>

      <div class="column is-one-quarter"></div>

      <div class="column is-one-quarter">
        <div class="is-flex is-align-items-center" style="gap: 0.5rem">
          <button
            class="c-button c-button--small c-button--secondary"
            :disabled="!isValidLeg"
            @click="addReturnLeg"
          >
            {{ $t("ReservationForm.AddReturnFlightLeg") }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="columns is-full-width"
      v-for="(leg, index) in legs"
      :key="index"
    >
      <div class="column is-one-quarter">
        {{ leg.departureAirport.name }}
      </div>
      <div class="column is-one-quarter">
        {{ leg.arrivalAirport.name }}
      </div>
      <div class="column">
        {{ formatDate(leg.departureDate) }}
      </div>
      <div class="column is-one-fifth">
        {{ $t("ReservationForm." + leg.dayPart) }}
        &nbsp;&nbsp;&nbsp;
        <div
          class="c-button c-button--red c-button--tiny is-inline-block"
          @click="removeLeg(leg)"
        >
          <i class="fa-solid fa-xmark is-danger"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FindAirport from "./FindAirport.vue";
import moment from "moment";
import { getDayParts } from "@/helpers/daypart.helper.js";

export default {
  components: {
    FindAirport
  },
  props: {
    selectedRoundtripType: {
      type: String,
      default: ""
    }
  },
  watch: {
    selectedRoundtripType() {
      this.legs = [];
    }
  },
  data() {
    let tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      tomorrow,
      departureAirport: "",
      arrivalAirport: "",
      departureDate: "",
      departureDateReturnFlight: "",
      selectedDayPart: "",
      selectedReturnDayPart: "",
      legs: [],
      minDepartureDate: tomorrow
    };
  },
  computed: {
    dayParts() {
      return getDayParts(this.$t);
    },
    isValidLeg() {
      if (this.selectedRoundtripType === "roundTrip") {
        return !!(
          this.departureAirport &&
          this.arrivalAirport &&
          this.selectedDayPart &&
          this.departureDate &&
          this.selectedReturnDayPart &&
          this.departureDateReturnFlight
        );
      } else {
        return !!(
          this.departureAirport &&
          this.arrivalAirport &&
          this.selectedDayPart &&
          this.departureDate
        );
      }
    },

    newLegAllowed() {
      if (
        this.selectedRoundtripType === "oneWay" ||
        this.selectedRoundtripType === "roundTrip"
      ) {
        return this.legs.length === 0;
      }
      return true;
    }
  },
  methods: {
    removeLeg(leg) {
      this.legs = this.legs.filter((l) => l !== leg);
      this.$emit("legs-selected", this.legs);
    },
    clearLegs() {
      this.legs = [];
      this.minDepartureDate = this.tomorrow;
    },
    setDayPart(dayPart) {
      this.dayPart = dayPart;
    },
    setDepartureAirport(airport) {
      this.departureAirport = airport;
    },
    setArrivalAirport(airport) {
      this.arrivalAirport = airport;
    },

    addReturnLeg() {
      if (this.isValidLeg) {
        const leg = {
          departureAirport: this.departureAirport,
          arrivalAirport: this.arrivalAirport,
          departureDate: moment(this.departureDate).format("YYYY-MM-DD"),
          dayPart: this.selectedDayPart
        };

        this.legs.push(leg);
        const returnLeg = {
          departureAirport: this.arrivalAirport,
          arrivalAirport: this.departureAirport,
          departureDate: moment(this.departureDateReturnFlight).format(
            "YYYY-MM-DD"
          ),
          dayPart: this.selectedReturnDayPart
        };

        this.legs.push(returnLeg);

        this.selectedDayPart = "";
        this.departureAirport = "";
        this.arrivalAirport = "";
        this.departureDate = "";
        this.departureDateReturnFlight = "";
        this.selectedReturnDayPart = "";

        this.$emit("legs-selected", this.legs);
      }
    },
    addLeg() {
      if (this.isValidLeg) {
        const leg = {
          departureAirport: this.departureAirport,
          arrivalAirport: this.arrivalAirport,
          departureDate: moment(this.departureDate).format("YYYY-MM-DD"),
          dayPart: this.selectedDayPart
        };

        this.legs.push(leg);
        if (this.selectedRoundtripType === "multiLeg") {
          this.departureAirport = this.arrivalAirport;
          this.minDepartureDate = this.departureDate;
          this.arrivalAirport = "";
        } else {
          this.departureAirport = "";
          this.arrivalAirport = "";
        }

        this.selectedDayPart = "";
        this.departureDate = "";
        this.$emit("legs-selected", this.legs);
      }
    }
  }
};
</script>

<style scoped>
/* Add component-specific styles here */
</style>
